import { Alert, Box, Card, CardContent, CardHeader, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { ArrayField, Datagrid, RecordContextProvider, TextField } from 'react-admin';
import { useParams } from 'react-router-dom';
import TimeAgoField from 'components/TimeAgoField';
import { UnitErrorBreakdownItem, useUnitErrorDetail } from 'services/api/useUnitErrorDetail';
import TimelineChart from './TimelineChart';

const breakdownLabel = (item: UnitErrorBreakdownItem): string => {
  const base = item.label ?? item.device_type ?? item.software_release ?? '';
  return item.count !== undefined ? `${base} (${item.count})` : String(base);
};

const BreakdownPanel = ({ title, items }: { title: string; items: UnitErrorBreakdownItem[] }) => (
  <Card sx={{ height: '100%' }}>
    <CardHeader title={title} />
    <CardContent>
      {items && items.length > 0 ? (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {items.map((item, index) => (
            <Chip key={`${title}.${index}`} label={breakdownLabel(item)} />
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="textSecondary">
          No data.
        </Typography>
      )}
    </CardContent>
  </Card>
);

const UnitErrorShow = () => {
  const { id } = useParams();
  const recordId = id ?? '';

  const { data, isLoading, error } = useUnitErrorDetail(recordId);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Unable to load error details.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        {data.message}
      </Typography>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardHeader title="Occurrences over time" />
            <CardContent>
              <TimelineChart data={data.timeline} />
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <BreakdownPanel title="Hardware types" items={data.breakdowns?.hardware_types ?? []} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <BreakdownPanel title="Software releases" items={data.breakdowns?.software_releases ?? []} />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Card>
            <CardHeader title="Affected units" />
            <CardContent>
              <RecordContextProvider value={{ id: recordId, affected_units: data.affected_units }}>
                <ArrayField source="affected_units">
                  <Datagrid bulkActionButtons={false} rowClick={false}>
                    <TextField source="serial" label="Serial" />
                    <TextField source="count" label="Occurrences" />
                    <TimeAgoField source="last_seen" label="Last seen" />
                  </Datagrid>
                </ArrayField>
              </RecordContextProvider>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UnitErrorShow;
